// Admin › Cost Centers — 2026-09-05
// Maintain the parent › sub tree (projects, collections, generic kinds), import from files,
// and set the date from which allocation becomes mandatory. Clients and deals normally arrive
// through "Import from ID Register". Since 2026-09-26 they can also be added here by hand with a
// lead code (LEAD-QT-YYYY-NNNN, deals -D1 …); the ERP never assigns CL-/DL- numbers. When the Register
// issues them, Edit switches the lead code to the CL-/DL- code.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Dropdown, Form, Input, InputNumber, List, Modal, Popconfirm, Row, Select, Space, Switch, Table, Tag, Typography, Upload, message,
} from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined, UploadOutlined, DownloadOutlined, SubnodeOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  listAllocCostCenters, listCostCenterTypes, createAllocCostCenter, updateAllocCostCenter, importCostCenters, listCostCenterImports,
  getCostCenterConfig, setCostCenterConfig, upsertCostCenterType, downloadCostCenterStatusSheet, CostCenterRow, CostCenterType, CostCenterImportResult,
} from '../../api';
import { useAuth } from '../../context/AuthContext';
import { invalidateCostCenterCache } from '../../components/CostCenterSelect';

const { Text } = Typography;
const REGISTER_TYPES = ['client', 'deal'];
const LEAD_CODE_RE: Record<string, RegExp> = { client: /^LEAD-QT-\d{4}-\d{4}$/i, deal: /^LEAD-QT-\d{4}-\d{4}-D\d+$/i };
const REGISTER_OR_LEAD_RE: Record<string, RegExp> = { client: /^(CL-\d{4}-\d{4}|LEAD-QT-\d{4}-\d{4})$/i, deal: /^(DL-\d{4}-\d{4}|LEAD-QT-\d{4}-\d{4}-D\d+)$/i };
const isManualRegisterRow = (r: CostCenterRow) => REGISTER_TYPES.includes(r.typeCode) && r.source !== 'id_register';

const CostCentersAdmin: React.FC = () => {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState<CostCenterRow[]>([]);
  const [types, setTypes] = useState<CostCenterType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [kindFilter, setKindFilter] = useState<string | undefined>();
  const [sourceFilter, setSourceFilter] = useState<string | undefined>();
  const [search, setSearch] = useState('');

  const [config, setConfig] = useState<{ requiredFrom: string | null; requiredNow: boolean } | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);

  const [createOpen, setCreateOpen] = useState<null | { parent?: CostCenterRow }>(null);
  const [createForm] = Form.useForm();
  const [editRow, setEditRow] = useState<CostCenterRow | null>(null);
  const [editForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [codeClash, setCodeClash] = useState<null | { message: string }>(null);

  const [importKind, setImportKind] = useState<'id-register' | 'template' | 'status' | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<CostCenterImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [importLogs, setImportLogs] = useState<any[]>([]);

  const [typeForm] = Form.useForm();
  const [savingType, setSavingType] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t, c, logs] = await Promise.all([listAllocCostCenters(true), listCostCenterTypes(), getCostCenterConfig(), listCostCenterImports().catch(() => [])]);
      setRows(r);
      setTypes(t);
      setConfig(c);
      setImportLogs(logs);
      invalidateCostCenterCache();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to load cost centers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const typeMap = useMemo(() => new Map(types.map((t) => [t.code, t])), [types]);
  const parentTypes = useMemo(() => types.filter((t) => !t.parentTypeCode), [types]);
  const childTypeOf = useMemo(() => {
    const m = new Map<string, CostCenterType>();
    for (const t of types) if (t.parentTypeCode) m.set(t.parentTypeCode, t);
    return m;
  }, [types]);
  // Kinds a person may add under a given node: its kind's sub kind (deals under clients too, since 2026-09-26) plus every "any parent" kind.
  const subKindsFor = (r: CostCenterRow): CostCenterType[] => {
    const out: CostCenterType[] = [];
    const direct = childTypeOf.get(r.typeCode);
    if (direct && direct.isActive) out.push(direct);
    for (const t of types) if (t.anyParent && t.isActive && !out.includes(t)) out.push(t);
    return out;
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!showInactive && !r.isActive) return false;
      const kind = r.parent ? (typeMap.get(r.typeCode)?.parentTypeCode || r.typeCode) : r.typeCode;
      if (kindFilter && kind !== kindFilter) return false;
      if (sourceFilter === 'manual-register' ? !isManualRegisterRow(r) : sourceFilter && r.source !== sourceFilter) return false;
      if (q && !`${r.label} ${r.code} ${r.externalRef || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, showInactive, kindFilter, sourceFilter, search, typeMap]);

  // ── create / edit ──
  const openCreate = (parent?: CostCenterRow) => {
    createForm.resetFields();
    if (parent) {
      const kinds = subKindsFor(parent);
      createForm.setFieldsValue({ typeCode: kinds[0]?.code, parentId: parent.id });
    }
    setCreateOpen({ parent });
  };

  const handleCreate = async () => {
    try {
      const v = await createForm.validateFields();
      setSaving(true);
      await createAllocCostCenter({
        code: v.code, name: v.name, typeCode: v.typeCode, parentId: v.parentId || null,
        externalRef: v.externalRef || null, notes: v.notes || null, sortOrder: v.sortOrder ?? 0,
      });
      message.success('Cost center created');
      setCreateOpen(null);
      load();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err.response?.data?.error || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (r: CostCenterRow) => {
    editForm.setFieldsValue({ code: r.code, name: r.name, externalRef: r.externalRef || '', notes: r.notes || '', sortOrder: r.sortOrder, isActive: r.isActive });
    setEditRow(r);
  };

  const handleEdit = async (ifCodeExists?: 'rename_old' | 'take_over') => {
    if (!editRow) return;
    try {
      const v = await editForm.validateFields();
      setSaving(true);
      const saved = await updateAllocCostCenter(editRow.id, {
        code: editRow.source === 'id_register' ? undefined : v.code,
        name: editRow.source === 'id_register' ? undefined : v.name,
        externalRef: editRow.source === 'id_register' ? undefined : (v.externalRef || null),
        notes: v.notes || null, sortOrder: v.sortOrder ?? 0, isActive: v.isActive,
        ifCodeExists,
      });
      message.success(saved?.takenOver ? `Saved: ${saved.code} took over from ${saved.takenOver.from} (${saved.takenOver.mrLines} line${saved.takenOver.mrLines === 1 ? '' : 's'} and ${saved.takenOver.subs} sub${saved.takenOver.subs === 1 ? '' : 's'} moved)` : 'Saved');
      setCodeClash(null);
      setEditRow(null);
      load();
    } catch (err: any) {
      if (err?.errorFields) return;
      const msg: string = err.response?.data?.error || 'Failed to save';
      // The Register code already sits on an inactive, unused row: ask whether to rename it away or take it over.
      if (!ifCodeExists && /inactive and unused/.test(msg)) { setCodeClash({ message: msg }); return; }
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (r: CostCenterRow, isActive: boolean, cascade: boolean) => {
    try {
      await updateAllocCostCenter(r.id, { isActive, cascade });
      message.success(isActive
        ? `${r.label} activated${cascade && r.childCount ? ` with its ${r.childCount} subs` : ''}`
        : `${r.label} deactivated${cascade && r.childCount ? ` with its ${r.childCount} subs` : ''} — hidden from new requests, kept on old lines`);
      load();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed');
    }
  };

  // ── imports ──
  const runImport = async (commit: boolean) => {
    if (!importKind || !importFile) return;
    setImporting(true);
    try {
      const res = await importCostCenters(importKind, importFile, commit);
      if (commit) {
        message.success(`Imported: ${res.added.length} added, ${res.updated.length} updated, ${res.unchanged} unchanged`);
        setImportKind(null); setImportFile(null); setImportPreview(null);
        load();
      } else {
        setImportPreview(res);
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // ── config ──
  const saveConfig = async (date: dayjs.Dayjs | null) => {
    setSavingConfig(true);
    try {
      const c = await setCostCenterConfig(date ? date.format('YYYY-MM-DD') : null);
      setConfig({ requiredFrom: c.requiredFrom, requiredNow: !!c.requiredFrom && dayjs(c.requiredFrom).isBefore(dayjs().add(1, 'day').startOf('day')) });
      message.success(date ? `Cost center becomes mandatory on ${date.format('YYYY-MM-DD')}` : 'Cost center stays optional');
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleAddType = async (v: any) => {
    setSavingType(true);
    try {
      await upsertCostCenterType({ code: v.code, label: v.label, parentTypeCode: v.anyParent ? null : (v.parentTypeCode || null), childRequired: !!v.childRequired, anyParent: !!v.anyParent, sortOrder: v.sortOrder ?? 50 });
      message.success('Kind saved');
      typeForm.resetFields();
      load();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to save kind');
    } finally {
      setSavingType(false);
    }
  };

  const columns = [
    {
      title: 'Cost center',
      key: 'label',
      width: 280,
      sorter: (x: CostCenterRow, y: CostCenterRow) => (x.parent?.name || x.name).localeCompare(y.parent?.name || y.name) || (x.parent ? 1 : 0) - (y.parent ? 1 : 0) || x.name.localeCompare(y.name),
      render: (_: any, r: CostCenterRow) => (
        <span style={{ paddingLeft: 24 * (r.depth ?? (r.parent ? 1 : 0)) }}>
          {r.parent ? <SubnodeOutlined style={{ color: '#aaa', marginRight: 6 }} /> : null}
          <strong>{r.name}</strong>
          {!r.isActive && <Tag style={{ marginLeft: 8 }}>inactive</Tag>}
        </span>
      ),
    },
    { title: 'Code', dataIndex: 'code', width: 150, sorter: (x: CostCenterRow, y: CostCenterRow) => (x.parent?.code || x.code).localeCompare(y.parent?.code || y.code) || (x.parent ? 1 : 0) - (y.parent ? 1 : 0) || x.code.localeCompare(y.code), render: (v: string) => <Text code>{v}</Text> },
    { title: 'Kind', key: 'kind', width: 130, render: (_: any, r: CostCenterRow) => <Tag>{typeMap.get(r.typeCode)?.label || r.typeCode}</Tag> },
    { title: 'Reference', dataIndex: 'externalRef', width: 140, ellipsis: true, render: (v: string) => v || <Text type="secondary">—</Text> },
    { title: 'Subs', dataIndex: 'childCount', width: 70, align: 'center' as const, render: (v: number, r: CostCenterRow) => (r.parent ? '' : v) },
    { title: 'Lines', dataIndex: 'usage', width: 70, align: 'center' as const },
    { title: 'Source', dataIndex: 'source', width: 170, render: (v: string, r: CostCenterRow) => (v === 'id_register' ? <Tag color="blue">ID Register</Tag> : isManualRegisterRow(r) ? <Tag color="gold">Manual · not in Register</Tag> : v === 'import' ? <Tag color="geekblue">Import</Tag> : <Tag>Manual</Tag>) },
    {
      title: 'Actions',
      key: 'actions',
      width: 230,
      render: (_: any, r: CostCenterRow) => (
        <Space size={4}>
          {subKindsFor(r).length > 0 && (
            <Button size="small" icon={<PlusOutlined />} onClick={() => openCreate(r)}>Sub</Button>
          )}
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
          {r.isActive ? (
            <Popconfirm
              title={r.childCount > 0 ? `Deactivate ${r.name} and everything under it (${r.childCount} direct subs)? They disappear from pickers; existing lines keep them.` : 'Deactivate? It disappears from pickers for new requests; existing lines keep it.'}
              onConfirm={() => setActive(r, false, r.childCount > 0)}
            >
              <Button size="small" danger>Deactivate</Button>
            </Popconfirm>
          ) : r.childCount > 0 ? (
            <Dropdown
              menu={{
                items: [
                  { key: 'all', label: `Activate with all ${r.childCount} subs`, onClick: () => setActive(r, true, true) },
                  { key: 'self', label: 'Activate parent only (switch subs on one by one)', onClick: () => setActive(r, true, false) },
                ],
              }}
            >
              <Button size="small" type="primary" ghost>Activate ▾</Button>
            </Dropdown>
          ) : (
            <Button size="small" type="primary" ghost onClick={() => setActive(r, true, false)}>Activate</Button>
          )}
        </Space>
      ),
    },
  ];

  const selectedType = Form.useWatch('typeCode', createForm);
  const selectedTypeDef = selectedType ? typeMap.get(selectedType) : undefined;
  const isRegisterKind = !!selectedType && REGISTER_TYPES.includes(selectedType);

  const parentCandidates = selectedTypeDef?.anyParent ? rows.filter((r) => r.isActive) : selectedTypeDef?.parentTypeCode ? rows.filter((r) => r.typeCode === selectedTypeDef.parentTypeCode && r.isActive) : [];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card size="small" title="Mandatory from">
            <Space wrap>
              <DatePicker
                value={config?.requiredFrom ? dayjs(config.requiredFrom) : null}
                onChange={(d) => saveConfig(d)}
                disabled={!isAdmin || savingConfig}
                allowClear
              />
              {config?.requiredNow
                ? <Tag color="red">Required now: every line must have a cost center before submit</Tag>
                : config?.requiredFrom
                  ? <Tag color="orange">Optional until {dayjs(config.requiredFrom).format('YYYY-MM-DD')}</Tag>
                  : <Tag>Optional (no date set)</Tag>}
            </Space>
            {!isAdmin && <div style={{ marginTop: 6 }}><Text type="secondary">Only an admin can change this date.</Text></div>}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small" title="Import">
            <Space wrap>
              <Button icon={<UploadOutlined />} onClick={() => { setImportKind('id-register'); setImportFile(null); setImportPreview(null); }}>Import from ID Register</Button>
              <Button icon={<UploadOutlined />} onClick={() => { setImportKind('template'); setImportFile(null); setImportPreview(null); }}>Import Cost Centers (template)</Button>
            </Space>
            <Space wrap style={{ marginTop: 8 }}>
              <Button icon={<DownloadOutlined />} onClick={() => downloadCostCenterStatusSheet().catch(() => message.error('Download failed'))}>Download status sheet</Button>
              <Button icon={<UploadOutlined />} onClick={() => { setImportKind('status'); setImportFile(null); setImportPreview(null); }}>Upload status sheet</Button>
            </Space>
            <div style={{ marginTop: 6 }}>
              <Text type="secondary">Clients and deals come from the AM ID Register (or are added here by hand, marked Manual until the Register takes them over); projects, collections and other kinds use the template. Imports add and update by code, never delete, and never change active/inactive. Activation is set here or in bulk with the status sheet (edit the Active column: Y / N).</Text>
            </div>
            {importLogs.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <Text type="secondary">Last import: {dayjs(importLogs[0].createdAt).format('YYYY-MM-DD HH:mm')} · {importLogs[0].source === 'id_register' ? 'ID Register' : importLogs[0].source === 'status' ? 'Status sheet' : 'Template'} · +{importLogs[0].added} / ~{importLogs[0].updated} / ={importLogs[0].unchanged}</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        title="Cost centers"
        extra={
          <Space wrap>
            <Input.Search placeholder="Search name / code" allowClear value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} />
            <Select allowClear placeholder="Kind" value={kindFilter} onChange={setKindFilter} style={{ width: 140 }} options={parentTypes.map((t) => ({ value: t.code, label: t.label }))} />
            <Select allowClear placeholder="Source" value={sourceFilter} onChange={setSourceFilter} style={{ width: 200 }} options={[
              { value: 'id_register', label: 'ID Register' },
              { value: 'manual-register', label: 'Manual · not in Register' },
              { value: 'manual', label: 'Manual (all)' },
              { value: 'import', label: 'Template import' },
            ]} />
            <span><Switch size="small" checked={showInactive} onChange={setShowInactive} /> <Text type="secondary">show inactive</Text></span>
            <Button icon={<ReloadOutlined />} onClick={load} />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate()}>Add parent</Button>
          </Space>
        }
      >
        <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={visible} pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200, 500], showTotal: (t: number) => `${t} rows` }} scroll={{ x: 1100 }} />
      </Card>

      {isAdmin && (
        <Card size="small" title="Kinds (types)" style={{ marginTop: 16 }}>
          <Table
            rowKey="code"
            size="small"
            pagination={false}
            dataSource={types}
            columns={[
              { title: 'Code', dataIndex: 'code', width: 130 },
              { title: 'Label', dataIndex: 'label' },
              { title: 'Sub of', dataIndex: 'parentTypeCode', width: 150, render: (v: string, t: CostCenterType) => (t.anyParent ? <Tag color="geekblue">anything</Tag> : v || <Text type="secondary">top level</Text>) },
              { title: 'Sub required', dataIndex: 'childRequired', width: 110, render: (v: boolean) => (v ? <Tag color="orange">yes</Tag> : 'no') },
              { title: 'Active', dataIndex: 'isActive', width: 80, render: (v: boolean) => (v ? 'yes' : 'no') },
            ]}
          />
          <Form form={typeForm} layout="inline" onFinish={handleAddType} style={{ marginTop: 12 }}>
            <Form.Item name="code" rules={[{ required: true, message: 'code' }]}><Input placeholder="code (e.g. event)" style={{ width: 150 }} /></Form.Item>
            <Form.Item name="label" rules={[{ required: true, message: 'label' }]}><Input placeholder="Label" style={{ width: 160 }} /></Form.Item>
            <Form.Item name="parentTypeCode"><Select allowClear placeholder="Sub of (blank = top level)" style={{ width: 200 }} options={types.filter((t) => !t.anyParent).map((t) => ({ value: t.code, label: t.label }))} /></Form.Item>
            <Form.Item name="anyParent" valuePropName="checked"><Switch checkedChildren="under anything" unCheckedChildren="fixed parent" /></Form.Item>
            <Form.Item name="childRequired" valuePropName="checked"><Switch checkedChildren="sub required" unCheckedChildren="sub optional" /></Form.Item>
            <Form.Item><Button htmlType="submit" icon={<SaveOutlined />} loading={savingType}>Add kind</Button></Form.Item>
          </Form>
        </Card>
      )}

      {/* Create */}
      <Modal open={!!createOpen} title={createOpen?.parent ? `Add sub under ${createOpen.parent.name}` : 'Add parent cost center'} onOk={handleCreate} onCancel={() => setCreateOpen(null)} confirmLoading={saving} okText="Create">
        <Form form={createForm} layout="vertical">
          <Form.Item name="typeCode" label="Kind" rules={[{ required: true }]}>
            <Select
              options={(createOpen?.parent
                ? subKindsFor(createOpen.parent)
                : types.filter((t) => t.isActive && !t.parentTypeCode && !t.anyParent)
              ).map((t) => ({ value: t.code, label: t.label }))}
            />
          </Form.Item>
          {selectedTypeDef?.parentTypeCode && (
            <Form.Item name="parentId" label="Parent" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" disabled={!!createOpen?.parent} options={parentCandidates.map((p) => ({ value: p.id, label: `${p.name} (${p.code})` }))} />
            </Form.Item>
          )}
          <Form.Item
            name="code"
            label="Code"
            rules={[
              { required: true },
              isRegisterKind
                ? { pattern: LEAD_CODE_RE[selectedType], message: selectedType === 'client' ? 'Lead code: LEAD-QT-YYYY-NNNN, the base quotation number without -R (the revision goes in Reference)' : 'Lead code: LEAD-QT-YYYY-NNNN-D1, base quotation number without -R' }
                : { pattern: /^[A-Za-z0-9][A-Za-z0-9._\-/ ]{0,49}$/, message: 'Letters, digits, dot, dash, slash' },
            ]}
            tooltip={isRegisterKind ? 'CL-/DL- numbers come only from the ID Register. Until AM issues one, use the lead code from the base quotation number (no -R revision; put the accepted revision in Reference); switch to the Register code later with Edit.' : 'Permanent once used on a line. Change the name later, never the code.'}
          >
            <Input placeholder={isRegisterKind ? (selectedType === 'client' ? 'LEAD-QT-2026-0221' : `${createOpen?.parent?.code?.startsWith('LEAD-') ? createOpen.parent.code : 'LEAD-QT-2026-0221'}-D1`) : 'e.g. RYAD or RYAD-17'} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="externalRef" label="Reference"><Input placeholder={selectedType === 'client' ? 'Category, e.g. Bridal' : selectedType === 'deal' ? 'Accepted quotation, e.g. QT-2026-0221' : 'season, look number, …'} /></Form.Item>
          {isRegisterKind && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`This ${selectedType} is not in the ID Register yet. When AM issues its ${selectedType === 'client' ? 'CL-' : 'DL-'} code, open Edit and put it in Code.`} />
          )}
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="sortOrder" label="Sort order" initialValue={0}><InputNumber /></Form.Item>
        </Form>
      </Modal>

      {/* Edit */}
      <Modal open={!!editRow} title={editRow ? `Edit ${editRow.label}` : ''} onOk={() => handleEdit()} onCancel={() => setEditRow(null)} confirmLoading={saving} okText="Save">
        {editRow?.source === 'id_register' && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Name and reference come from the ID Register. Re-import the register to change them." />}
        {editRow && isManualRegisterRow(editRow) && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message={`When AM issues the ${editRow.typeCode === 'client' ? 'CL-' : 'DL-'} code in the ID Register, put it in Code and save.`} />
        )}
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="code"
            label="Code"
            rules={[
              { required: true },
              editRow && isManualRegisterRow(editRow)
                ? { pattern: REGISTER_OR_LEAD_RE[editRow.typeCode], message: editRow.typeCode === 'client' ? 'CL-YYYY-NNNN from the Register, or LEAD-QT-YYYY-NNNN' : 'DL-YYYY-NNNN from the Register, or LEAD-QT-YYYY-NNNN-D1' }
                : { pattern: /^[A-Za-z0-9][A-Za-z0-9._\-/ ]{0,49}$/, message: 'Letters, digits, dot, dash, slash' },
            ]}
            tooltip="Changing a code is logged. Use the new code in any template you upload afterwards, or the import will treat the old code as a new entry."
          >
            <Input disabled={editRow?.source === 'id_register'} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input disabled={editRow?.source === 'id_register'} /></Form.Item>
          <Form.Item name="externalRef" label="Reference"><Input disabled={editRow?.source === 'id_register'} /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="sortOrder" label="Sort order"><InputNumber /></Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>

      {/* Register code already on an inactive, unused row */}
      <Modal
        open={!!codeClash}
        title="That code already exists"
        onCancel={() => setCodeClash(null)}
        footer={[
          <Button key="cancel" onClick={() => setCodeClash(null)}>Cancel</Button>,
          <Button key="rename" loading={saving} onClick={() => handleEdit('rename_old')}>Rename the old one</Button>,
          <Button key="take" type="primary" loading={saving} onClick={() => handleEdit('take_over')}>Take it over</Button>,
        ]}
      >
        <p>{codeClash?.message}</p>
        <Text type="secondary">Rename the old one: it keeps its history as &lt;code&gt;-OLD and stays inactive. Take it over: that row becomes this {editRow?.typeCode}, with its lines and subs; the lead row is left inactive. Nothing is deleted.</Text>
      </Modal>

      {/* Import */}
      <Modal
        open={!!importKind}
        title={importKind === 'id-register' ? 'Import clients & deals from SL Master ID Register' : importKind === 'status' ? 'Upload status sheet (Active Y / N)' : 'Import cost centers from template'}
        onCancel={() => { setImportKind(null); setImportFile(null); setImportPreview(null); }}
        width={720}
        footer={[
          <Button key="cancel" onClick={() => { setImportKind(null); setImportFile(null); setImportPreview(null); }}>Cancel</Button>,
          <Button key="preview" onClick={() => runImport(false)} disabled={!importFile} loading={importing && !importPreview}>Preview (dry run)</Button>,
          <Button key="commit" type="primary" onClick={() => runImport(true)} disabled={!importPreview || (importPreview.added.length === 0 && importPreview.updated.length === 0)} loading={importing && !!importPreview}>
            Apply {importPreview ? `(${importPreview.added.length} add, ${importPreview.updated.length} update)` : ''}
          </Button>,
        ]}
      >
        <Upload.Dragger
          accept=".xlsx"
          multiple={false}
          maxCount={1}
          beforeUpload={(file) => { setImportFile(file as any); setImportPreview(null); return false; }}
          onRemove={() => { setImportFile(null); setImportPreview(null); }}
          fileList={importFile ? [{ uid: '1', name: importFile.name } as any] : []}
        >
          <p className="ant-upload-text">Drop the .xlsx here or click to choose</p>
          <p className="ant-upload-hint">
            {importKind === 'id-register'
              ? 'Sheets "01 Client Master" and "02 Deal Register" are read. Names and codes only; activation is not touched. The workbook is never modified.'
              : importKind === 'status'
                ? 'The sheet you downloaded: only the Active column (Y / N) is applied, to any code including clients and deals.'
                : 'Sheet "Cost Centers": Type | Parent Code | Parent Name | Sub Code | Sub Name | Reference | Notes | Active | Sub Kind (optional). Parent Code may be any existing code, at any depth.'}
          </p>
        </Upload.Dragger>
        {importPreview && (
          <div style={{ marginTop: 12 }}>
            <Space wrap style={{ marginBottom: 8 }}>
              <Tag color="green">Add {importPreview.added.length}</Tag>
              <Tag color="blue">Update {importPreview.updated.length}</Tag>
              <Tag>Unchanged {importPreview.unchanged}</Tag>
              <Tag color={importPreview.errors.length ? 'red' : 'default'}>Errors {importPreview.errors.length}</Tag>
            </Space>
            {importPreview.errors.length > 0 && (
              <List size="small" bordered style={{ marginBottom: 8, maxHeight: 160, overflow: 'auto' }} dataSource={importPreview.errors} renderItem={(e) => <List.Item><Text type="danger">{e.row}: {e.message}</Text></List.Item>} />
            )}
            {importPreview.updated.length > 0 && (
              <List size="small" bordered style={{ marginBottom: 8, maxHeight: 160, overflow: 'auto' }} header="Updates" dataSource={importPreview.updated} renderItem={(u) => <List.Item><Text code>{u.code}</Text>&nbsp;{u.label} — {u.changes.join('; ')}</List.Item>} />
            )}
            {importPreview.added.length > 0 && (
              <List size="small" bordered style={{ maxHeight: 200, overflow: 'auto' }} header="Additions" dataSource={importPreview.added} renderItem={(a) => <List.Item><Text code>{a.code}</Text>&nbsp;{a.label}</List.Item>} />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CostCentersAdmin;
